import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../utils/api';

export const fetchDashboard = createAsyncThunk('admin/dashboard', async (_, { rejectWithValue }) => {
  try {
    const { data } = await API.get('/admin/dashboard');
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const fetchAdminOrders = createAsyncThunk('admin/orders', async (params, { rejectWithValue }) => {
  try {
    const { data } = await API.get('/admin/orders', { params });
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const updateOrderStatus = createAsyncThunk('admin/updateOrderStatus', async ({ orderId, statusData }, { rejectWithValue }) => {
  try {
    const { data } = await API.put(`/admin/orders/${orderId}/status`, statusData);
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const fetchAdminUsers = createAsyncThunk('admin/users', async (params, { rejectWithValue }) => {
  try {
    const { data } = await API.get('/admin/users', { params });
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const fetchAdminUserDetails = createAsyncThunk('admin/userDetails', async (id, { rejectWithValue }) => {
  try {
    const { data } = await API.get(`/admin/users/${id}`);
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to load user details');
  }
});

export const createProduct = createAsyncThunk('admin/createProduct', async (formData, { rejectWithValue }) => {
  try {
    const { data } = await API.post('/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create product');
  }
});

export const adminUpdateProduct = createAsyncThunk('admin/updateProduct', async ({ id, formData }, { rejectWithValue }) => {
  try {
    const { data } = await API.put(`/products/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update product');
  }
});

export const deleteProduct = createAsyncThunk('admin/deleteProduct', async (id, { rejectWithValue }) => {
  try {
    await API.delete(`/products/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    dashboard: null,
    orders: [],
    users: [],
    userDetails: null,
    userDetailsLoading: false,
    orderPagination: null,
    userPagination: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearUserDetails: (state) => { state.userDetails = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.pending, (state) => { state.loading = true; })
      .addCase(fetchDashboard.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.dashboard = payload.stats;
      })
      .addCase(fetchDashboard.rejected, (state) => { state.loading = false; })
      .addCase(fetchAdminOrders.fulfilled, (state, { payload }) => {
        state.orders = payload.orders;
        state.orderPagination = payload.pagination;
      })
      .addCase(updateOrderStatus.fulfilled, (state, { payload }) => {
        const idx = state.orders.findIndex(o => o._id === payload.order._id);
        if (idx !== -1) state.orders[idx] = payload.order;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, { payload }) => {
        state.users = payload.users;
        state.userPagination = payload.pagination;
      })
      .addCase(fetchAdminUserDetails.pending, (state) => { state.userDetailsLoading = true; })
      .addCase(fetchAdminUserDetails.fulfilled, (state, { payload }) => {
        state.userDetailsLoading = false;
        state.userDetails = payload;
      })
      .addCase(fetchAdminUserDetails.rejected, (state) => { state.userDetailsLoading = false; })
      .addCase(deleteProduct.fulfilled, (state, { payload }) => {
        // product removed
      });
  },
});

export default adminSlice.reducer;
export const { clearUserDetails } = adminSlice.actions;
